import { UseMutationOptions } from '@tanstack/react-query';

import { SendInput } from 'domains/campaigns/types';
import { SegmentDefinition } from 'domains/segments/types';
import { CampaignType, CuredApiPaths, ApiMethods } from 'models/enums';
import { PathWithID } from 'models/types';

import useMutation from '../useMutation';

import MutationKeys from './keys';

export type UpdateAndResumeCampaignMutationInput = {
    name: string;
    type: CampaignType;
    sends: SendInput[];
    // Campaign-level exit criteria: the snapshotted segment definition (or null to clear).
    exitSegmentDefinition?: SegmentDefinition | null;
};

type UpdateAndResumeCampaignResponse = { id: string };

/**
 * Updates and resumes a paused campaign.
 * - PUT /campaigns/:campaignId?resume=true
 * @param campaignId Campaign ID to update and resume
 * @param mutateOptions react-query useMutation options
 * @returns the react-query useMutation results
 * See https://react-query-v3.tanstack.com/reference/useMutation for options and return value
 */
const useUpdateAndResumeCampaign = (
    campaignId: string,
    mutateOptions?: UseMutationOptions<UpdateAndResumeCampaignResponse, Error, UpdateAndResumeCampaignMutationInput>
) => {
    const basePath = new PathWithID(CuredApiPaths.CAMPAIGNS, campaignId);
    const apiPath = `${basePath.url}?resume=true`;

    return useMutation<UpdateAndResumeCampaignMutationInput, UpdateAndResumeCampaignResponse>(
        MutationKeys.UPDATE_CAMPAIGN,
        apiPath,
        mutateOptions,
        ApiMethods.PUT
    );
};

export default useUpdateAndResumeCampaign;
